import { I18nContext } from '@hopin-team/provider-translation';
import { intlFormat } from 'date-fns';
import * as locales from 'date-fns/locale';
import { useContext, useMemo } from 'react';

const TWELVE_HOUR_LOCALES_OVERRIDES = ['en-GB'];

export const useDateLocale: (
  browserLocale: string,
) => Locale = browserLocale => {
  const { i18n } = useContext(I18nContext);

  const localesByCode: { [lang: string]: Locale } = useMemo(
    () => ({
      ...Object.values(locales)
        .filter(value => value.code)
        .reduce<{ [lang: string]: Locale }>(
          (prev, value) => ({
            ...prev,
            [value.code?.toLowerCase() || '']: value,
          }),
          {},
        ),
      ...locales,
    }),
    [],
  );

  // The default i18n language we have is english, but that could mean GB/US/AUS/IN....
  // If their browser locale specifies, use that value, otherwise fallback to US.
  const englishDefault =
    i18n.language === 'en'
      ? browserLocale.includes('en')
        ? browserLocale
        : 'en-us'
      : i18n.language;

  return (
    localesByCode[englishDefault] ||
    localesByCode[browserLocale.toLowerCase()] ||
    localesByCode['en-us']
  );
};

export const getFormattedTimeByLocale =
  (locale?: string) =>
  (date: Date, shouldShowMinute = true, shouldShowSeconds = false) => {
    // TS debauchery because date-fns doesn't recognise `hourCycle` as a valid option
    // Needing to specify h12 because hour12 formats 12pm as 0pm which is weird.
    const formatOptions = {
      hour: 'numeric',
      ...(TWELVE_HOUR_LOCALES_OVERRIDES.includes(locale || '')
        ? { hourCycle: 'h12' }
        : {}),
      ...(shouldShowMinute ? { minute: '2-digit' } : {}),
      ...(shouldShowSeconds ? { second: '2-digit' } : {}),
    } as unknown as any;
    return intlFormat(date, formatOptions, { locale }).toUpperCase();
  };

export const useDateFormats = () => {
  const locale = useDateLocale(navigator.language);

  return { getFormattedTime: getFormattedTimeByLocale(locale.code) };
};
